import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Images, ArrowUpRight } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { Reveal } from "@/components/KineticLines";

const SAMPLE_COVERS = {
  residential: [
    { id: "d1", title: "Modern Colonial Refresh", cover: "https://images.pexels.com/photos/7601153/pexels-photo-7601153.jpeg?auto=compress&cs=tinysrgb&w=1920&q=85", photo_count: 8 },
    { id: "d2", title: "Lakeside Full Re-Side", cover: "https://images.unsplash.com/photo-1781920872332-e9ccdde6fd3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3VzZSUyMGRhcmslMjBzaWRpbmd8ZW58MHx8fHwxNzg2MzY4MzY4fDA&ixlib=rb-4.1.0&q=85", photo_count: 12 },
    { id: "d3", title: "Dark Cedar Contemporary", cover: "https://images.unsplash.com/photo-1774177873629-4174653c2f5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob3VzZSUyMGRhcmslMjBzaWRpbmd8ZW58MHx8fHwxNzg2MzY4MzY4fDA&ixlib=rb-4.1.0&q=85", photo_count: 6 },
  ],
  commercial: [
    { id: "d4", title: "Downtown Mixed-Use Facade", cover: "https://images.pexels.com/photos/10486073/pexels-photo-10486073.jpeg?auto=compress&cs=tinysrgb&w=1920&q=85", photo_count: 15 },
    { id: "d5", title: "Office Park Re-Clad", cover: "https://images.unsplash.com/photo-1774177873629-4174653c2f5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob3VzZSUyMGRhcmslMjBzaWRpbmd8ZW58MHx8fHwxNzg2MzY4MzY4fDA&ixlib=rb-4.1.0&q=85", photo_count: 9 },
    { id: "d6", title: "Retail Strip Renovation", cover: "https://images.pexels.com/photos/34670926/pexels-photo-34670926.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", photo_count: 11 },
  ],
};

export const Gallery = ({ category }) => {
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/projects", { params: { category } })
      .then(({ data }) => { if (!cancelled) setProjects(data); })
      .catch(() => { if (!cancelled) setProjects([]); });
    return () => { cancelled = true; };
  }, [category]);

  const demo = projects !== null && projects.length === 0;
  const items = demo
    ? SAMPLE_COVERS[category]
    : (projects || []).map((p) => ({ id: p.id, title: p.title, cover: p.cover_path ? mediaUrl(p.cover_path) : null, photo_count: p.photo_count }));

  const Card = ({ item, i }) => {
    const inner = (
      <>
        <div className="relative overflow-hidden border border-white/15 rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.25)]">
          {item.cover ? (
            <img src={item.cover} alt={item.title} loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105" />
          ) : (
            <div className="w-full aspect-[4/3] bg-white/5 flex items-center justify-center"><Images size={28} className="text-white/20" aria-hidden="true" /></div>
          )}
          <span className="absolute top-3 right-3 bg-[#171914]/80 backdrop-blur-sm rounded-full px-3 py-1 text-[10px] font-bold tracking-widest uppercase text-white/90" data-testid={`project-count-${i}`}>
            {item.photo_count} photo{item.photo_count === 1 ? "" : "s"}
          </span>
          {!demo && (
            <span className="absolute bottom-3 right-3 w-9 h-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
              <ArrowUpRight size={17} aria-hidden="true" />
            </span>
          )}
        </div>
        <h3 className="mt-4 font-display text-2xl md:text-3xl tracking-wide group-hover:text-primary transition-colors duration-300">{item.title}</h3>
      </>
    );
    return (
      <motion.div
        key={item.id}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
      >
        {demo ? (
          <div className="group" data-testid={`project-card-${i}`}>{inner}</div>
        ) : (
          <Link to={`/project/${item.id}`} className="group block" data-testid={`project-card-${i}`}>{inner}</Link>
        )}
      </motion.div>
    );
  };

  return (
    <section id="gallery" data-testid={`gallery-${category}`} className="py-24 md:py-36 px-5 md:px-10 max-w-7xl mx-auto">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-primary text-xs font-bold tracking-[0.3em] uppercase mb-3">Our Work</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.9]">PROJECT<br />GALLERY</h2>
          </div>
          <p className="text-white/50 text-sm max-w-xs md:text-right">Every project is a full photo set. Open one to see the whole job, start to finish.</p>
        </div>
      </Reveal>
      {projects === null ? (
        <p className="text-white/40 text-sm py-16 text-center">Loading projects...</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-6" data-testid="gallery-grid">
          {items.map((item, i) => <Card key={item.id} item={item} i={i} />)}
        </div>
      )}
      {demo && (
        <p className="mt-8 text-xs text-white/40 tracking-wide" data-testid="gallery-sample-note">
          Sample projects shown — real work appears here as soon as projects are created in the admin panel.
        </p>
      )}
    </section>
  );
};
